import type { SalesCaseId, CrmOfferId, ScheduledActivityId, LeadId } from '@previo/codebooks'
import { type DownloadableFile, HttpMethod } from '@previo/core'
import Http from '~/http/Http'
import { APIPaths } from '~/http/APIPaths'
import type {
  AddNoteRequest,
  CalculateOfferRequest,
  CalculateOfferResponse,
  ChangePotentialRequest,
  ChangeSalesmanRequest,
  ChangeStatusRequest,
  ChangelogResponse,
  CreateDealRequest,
  CreateLeadRequest,
  CreateResponse,
  CreateScheduledActivityRequest,
  DetailResponse,
  DisqualifyRequest,
  EditNoteRequest,
  GetChangelogQuery,
  GetHistoryQuery,
  GetNotesQuery,
  HistoryLogResponse,
  LinkContactPersonRequest,
  LinkNewPropertyRequest,
  LinkPropertyRequest,
  LostRequest,
  CreateOfferRequest,
  CreateOfferResponse,
  NoteResponse,
  OfferDetailResponse,
  OfferSummaryResponse,
  PaginatedResponse,
  ReplacePropertyRequest,
  SalesCaseByLeadResponse,
  ScheduledActivitiesQuery,
  ScheduledActivityResponse,
  UpdateLeadContactRequest,
  UpdateLeadRequest,
  UpdateOfferRequest,
  UpdatePropertyRequest,
  UpdateScheduledActivityRequest,
} from './types'

export class SalesCaseAPI extends Http {
  detail = (salesCaseId: SalesCaseId): Promise<DetailResponse> => {
    return this.callAuthAPI<null, DetailResponse>(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_DETAIL,
      params: { salesCaseId },
    })
  }

  findByLead = (leadId: LeadId): Promise<SalesCaseByLeadResponse> => {
    return this.callAuthAPI<null, SalesCaseByLeadResponse>(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_BY_LEAD,
      params: { leadId },
    })
  }

  createLead = (data: CreateLeadRequest): Promise<CreateResponse> => {
    return this.callAuthAPI<CreateLeadRequest, CreateResponse>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_LEAD_CREATE },
      data,
    )
  }

  createDeal = (data: CreateDealRequest): Promise<CreateResponse> => {
    return this.callAuthAPI<CreateDealRequest, CreateResponse>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_DEAL_CREATE },
      data,
    )
  }

  createOffer = (data: CreateOfferRequest): Promise<CreateOfferResponse> => {
    return this.callAuthAPI<CreateOfferRequest, CreateOfferResponse>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_OFFER_CREATE },
      data,
    )
  }

  sendOffer = (offerId: CrmOfferId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.POST, {
      path: APIPaths.SALES_CASE_OFFER_SEND,
      params: { offerId },
    })
  }

  offerSummary = (offerId: CrmOfferId): Promise<OfferSummaryResponse> => {
    return this.callAuthAPI<null, OfferSummaryResponse>(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_OFFER_SUMMARY,
      params: { offerId },
    })
  }

  offerDetail = (offerId: CrmOfferId): Promise<OfferDetailResponse> => {
    return this.callAuthAPI<null, OfferDetailResponse>(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_OFFER_DETAIL,
      params: { offerId },
    })
  }

  offerPdf = (offerId: CrmOfferId): Promise<DownloadableFile> => {
    return this.callAuthAPI<null, DownloadableFile>(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_OFFER_PDF,
      params: { offerId },
    })
  }

  updateOffer = (offerId: CrmOfferId, data: UpdateOfferRequest): Promise<null> => {
    return this.callAuthAPI<UpdateOfferRequest, null>(
      HttpMethod.PUT,
      { path: APIPaths.SALES_CASE_OFFER_UPDATE, params: { offerId } },
      data,
    )
  }

  deleteOffer = (salesCaseId: SalesCaseId, offerId: CrmOfferId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.DELETE, {
      path: APIPaths.SALES_CASE_OFFER_DELETE,
      params: { salesCaseId, offerId },
    })
  }

  calculateOffer = (data: CalculateOfferRequest): Promise<CalculateOfferResponse> => {
    return this.callAuthAPI<CalculateOfferRequest, CalculateOfferResponse>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_OFFER_CALCULATE },
      data,
    )
  }

  changeSalesman = (salesCaseId: SalesCaseId, data: ChangeSalesmanRequest): Promise<null> => {
    return this.callAuthAPI<ChangeSalesmanRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_CHANGE_SALESMAN, params: { salesCaseId } },
      data,
    )
  }

  changePotential = (salesCaseId: SalesCaseId, data: ChangePotentialRequest): Promise<null> => {
    return this.callAuthAPI<ChangePotentialRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_CHANGE_POTENTIAL, params: { salesCaseId } },
      data,
    )
  }

  changeStatus = (salesCaseId: SalesCaseId, data: ChangeStatusRequest): Promise<null> => {
    return this.callAuthAPI<ChangeStatusRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_CHANGE_STATUS, params: { salesCaseId } },
      data,
    )
  }

  updateLead = (salesCaseId: SalesCaseId, data: UpdateLeadRequest): Promise<null> => {
    return this.callAuthAPI<UpdateLeadRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_UPDATE_LEAD, params: { salesCaseId } },
      data,
    )
  }

  updateProperty = (salesCaseId: SalesCaseId, data: UpdatePropertyRequest): Promise<null> => {
    return this.callAuthAPI<UpdatePropertyRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_UPDATE_PROPERTY, params: { salesCaseId } },
      data,
    )
  }

  disqualify = (salesCaseId: SalesCaseId, data: DisqualifyRequest): Promise<null> => {
    return this.callAuthAPI<DisqualifyRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_DISQUALIFY, params: { salesCaseId } },
      data,
    )
  }

  linkContactPerson = (salesCaseId: SalesCaseId, data: LinkContactPersonRequest): Promise<null> => {
    return this.callAuthAPI<LinkContactPersonRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_LINK_CONTACT_PERSON, params: { salesCaseId } },
      data,
    )
  }

  unlinkContactPerson = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_UNLINK_CONTACT_PERSON,
      params: { salesCaseId },
    })
  }

  updateLeadContact = (salesCaseId: SalesCaseId, data: UpdateLeadContactRequest): Promise<null> => {
    return this.callAuthAPI<UpdateLeadContactRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_UPDATE_LEAD_CONTACT, params: { salesCaseId } },
      data,
    )
  }

  linkProperty = (salesCaseId: SalesCaseId, data: LinkPropertyRequest): Promise<null> => {
    return this.callAuthAPI<LinkPropertyRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_LINK_PROPERTY, params: { salesCaseId } },
      data,
    )
  }

  replaceProperty = (salesCaseId: SalesCaseId, data: ReplacePropertyRequest): Promise<null> => {
    return this.callAuthAPI<ReplacePropertyRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_REPLACE_PROPERTY, params: { salesCaseId } },
      data,
    )
  }

  linkNewProperty = (salesCaseId: SalesCaseId, data: LinkNewPropertyRequest): Promise<null> => {
    return this.callAuthAPI<LinkNewPropertyRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_LINK_NEW_PROPERTY, params: { salesCaseId } },
      data,
    )
  }

  unlinkProperty = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_UNLINK_PROPERTY,
      params: { salesCaseId },
    })
  }

  delete = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.DELETE, {
      path: APIPaths.SALES_CASE_DELETE,
      params: { salesCaseId },
    })
  }

  convertToDeal = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_CONVERT_TO_DEAL,
      params: { salesCaseId },
    })
  }

  convertToLead = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_CONVERT_TO_LEAD,
      params: { salesCaseId },
    })
  }

  merge = (salesCaseId: SalesCaseId, data: { salesCaseIds: SalesCaseId[] }): Promise<void> => {
    return this.callAuthAPI<{ salesCaseIds: SalesCaseId[] }, void>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_MERGE, params: { salesCaseId } },
      data,
    )
  }

  moveToOfferStageTransition = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_STAGE_MOVE_TO_OFFER,
      params: { salesCaseId },
    })
  }

  startImplementation = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_START_IMPLEMENTATION,
      params: { salesCaseId },
    })
  }

  continueImplementation = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_CONTINUE_IMPLEMENTATION,
      params: { salesCaseId },
    })
  }

  finishImplementation = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_FINISH_IMPLEMENTATION,
      params: { salesCaseId },
    })
  }

  markAsLost = (salesCaseId: SalesCaseId, data: LostRequest): Promise<null> => {
    return this.callAuthAPI<LostRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_MARK_AS_LOST, params: { salesCaseId } },
      data,
    )
  }

  renew = (salesCaseId: SalesCaseId): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_RENEW,
      params: { salesCaseId },
    })
  }

  getHistory = async (
    salesCaseId: SalesCaseId,
    query?: GetHistoryQuery,
  ): Promise<PaginatedResponse<HistoryLogResponse>> => {
    const response = await this.callAuthAPI<
      null,
      { historyItems: HistoryLogResponse[]; total: number }
    >(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_HISTORY,
      params: { salesCaseId },
      query,
    })
    return { data: response.historyItems, total: response.total }
  }

  getNotes = async (
    salesCaseId: SalesCaseId,
    query?: GetNotesQuery,
  ): Promise<PaginatedResponse<NoteResponse>> => {
    const response = await this.callAuthAPI<null, { notes: NoteResponse[]; total: number }>(
      HttpMethod.GET,
      {
        path: APIPaths.SALES_CASE_NOTES,
        params: { salesCaseId },
        query,
      },
    )
    return { data: response.notes, total: response.total }
  }

  getChangelog = async (
    salesCaseId: SalesCaseId,
    query?: GetChangelogQuery,
  ): Promise<PaginatedResponse<ChangelogResponse>> => {
    const response = await this.callAuthAPI<
      null,
      { changelogs: ChangelogResponse[]; total: number }
    >(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_CHANGELOG,
      params: { salesCaseId },
      query,
    })
    return { data: response.changelogs, total: response.total }
  }

  addNote = (salesCaseId: SalesCaseId, data: AddNoteRequest): Promise<null> => {
    return this.callAuthAPI<AddNoteRequest, null>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_NOTE_CREATE, params: { salesCaseId } },
      data,
    )
  }

  editNote = (salesCaseId: SalesCaseId, noteId: number, data: EditNoteRequest): Promise<null> => {
    return this.callAuthAPI<EditNoteRequest, null>(
      HttpMethod.PATCH,
      { path: APIPaths.SALES_CASE_NOTE_UPDATE, params: { salesCaseId, noteId } },
      data,
    )
  }

  deleteNote = (salesCaseId: SalesCaseId, noteId: number): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.DELETE, {
      path: APIPaths.SALES_CASE_NOTE_DELETE,
      params: { salesCaseId, noteId },
    })
  }

  getScheduledActivitiesForSalesCase = async (
    salesCaseId: SalesCaseId,
    query?: ScheduledActivitiesQuery,
  ): Promise<PaginatedResponse<ScheduledActivityResponse>> => {
    const response = await this.callAuthAPI<
      null,
      { scheduledActivities: ScheduledActivityResponse[]; total: number }
    >(HttpMethod.GET, {
      path: APIPaths.SALES_CASE_SCHEDULED_ACTIVITIES,
      params: { salesCaseId },
      query,
    })
    return { data: response.scheduledActivities, total: response.total }
  }

  createScheduledActivity = (
    salesCaseId: SalesCaseId,
    data: CreateScheduledActivityRequest,
  ): Promise<null> => {
    return this.callAuthAPI<CreateScheduledActivityRequest, null>(
      HttpMethod.POST,
      { path: APIPaths.SALES_CASE_SCHEDULE_ACTIVITY, params: { salesCaseId } },
      data,
    )
  }

  completeScheduledActivity = (
    salesCaseId: SalesCaseId,
    scheduledActivityId: ScheduledActivityId,
  ): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_COMPLETE_SCHEDULED_ACTIVITY,
      params: { salesCaseId, scheduledActivityId },
    })
  }

  markScheduledActivityIncomplete = (
    salesCaseId: SalesCaseId,
    scheduledActivityId: ScheduledActivityId,
  ): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.PATCH, {
      path: APIPaths.SALES_CASE_MARK_SCHEDULED_ACTIVITY_AS_INCOMPLETE,
      params: { salesCaseId, scheduledActivityId },
    })
  }

  cancelScheduledActivity = (
    salesCaseId: SalesCaseId,
    scheduledActivityId: ScheduledActivityId,
  ): Promise<null> => {
    return this.callAuthAPI<null, null>(HttpMethod.DELETE, {
      path: APIPaths.SALES_CASE_CANCEL_SCHEDULED_ACTIVITY,
      params: { salesCaseId, scheduledActivityId },
    })
  }

  updateScheduledActivity = (
    salesCaseId: SalesCaseId,
    scheduledActivityId: ScheduledActivityId,
    data: UpdateScheduledActivityRequest,
  ): Promise<null> => {
    return this.callAuthAPI<UpdateScheduledActivityRequest, null>(
      HttpMethod.PUT,
      {
        path: APIPaths.SALES_CASE_UPDATE_SCHEDULED_ACTIVITY,
        params: { salesCaseId, scheduledActivityId },
      },
      data,
    )
  }
}

export const useSalesCaseAPI = (): SalesCaseAPI => {
  return new SalesCaseAPI()
}
